import { app } from "../App";
import {
  collection,
  limit,
  onSnapshot,
  query,
  where
  } from "firebase/firestore";
import { db } from "../App";
import { deleteMusicFile, uploadMusicFile } from "../services/musicService";
import { useCallback, useEffect, useState } from "react";

/**
 * Interfejs dla danych muzyki prezentacji z Firestore
 */
interface PresentationMusicData {
  id: string;
  tournamentId: string;
  categoryId: string;
  presentationId: string;
  file: {
    originalName: string;
    storagePath: string;
    mimeType: string;
    size: number;
    duration?: number;
  };
  status: "uploaded" | "processing" | "ready" | "error";
  uploadedBy: "registration" | "admin";
  createdAt: any;
  updatedAt: any;
}

/**
 * Hook zarządzający muzyką dla prezentacji
 * 
 * @param tournamentId - ID turnieju
 * @param categoryId - ID kategorii
 * @param presentationId - ID prezentacji
 * @returns Obiekt z danymi muzyki, statusem uploadu i funkcjami
 */
export function usePresentationMusic(
  tournamentId: string,
  categoryId: string,
  presentationId: string
) {
  const [music, setMusic] = useState<PresentationMusicData | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Pobieranie informacji o muzyce z Firestore
  useEffect(() => {
    if (!tournamentId || !categoryId || !presentationId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    // Pobierz wszystkie dokumenty muzyki dla tej prezentacji (niezależnie od statusu)
    const musicQuery = query(
      collection(db, "presentationMusic"),
      where("tournamentId", "==", tournamentId),
      where("categoryId", "==", categoryId),
      where("presentationId", "==", presentationId),
      limit(1) // Powinien być tylko jeden dokument na prezentację
    );

    const unsubscribe = onSnapshot(
      musicQuery,
      (snapshot) => {
        if (!snapshot.empty) {
          const doc = snapshot.docs[0];
          setMusic({
            id: doc.id,
            ...doc.data(),
          } as PresentationMusicData);
        } else {
          setMusic(null);
        }
        setIsLoading(false);
        setError(null);
      },
      (err) => {
        console.error("Błąd podczas pobierania muzyki:", err);
        setError("Nie udało się pobrać informacji o muzyce");
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [tournamentId, categoryId, presentationId]);

  // Funkcja uploadu muzyki
  const uploadMusic = useCallback(
    async (file: File) => {
      if (!tournamentId || !categoryId || !presentationId) {
        setError("Brak wymaganych danych do uploadu");
        return;
      }

      setIsUploading(true);
      setError(null);

      try {
        const result = await uploadMusicFile(tournamentId, categoryId, presentationId, file, app);

        if (!result.success) {
          setError(result.error || "Nie udało się przesłać pliku");
          setIsUploading(false);
          return;
        }

        // Upload zakończony pomyślnie - dodaj krótkie opóźnienie przed ustawieniem isUploading = false
        // To daje czas na aktualizację dokumentu presentationMusic w Firestore (status: "uploaded")
        // i zapewnia lepszą synchronizację między frontendem a backendem
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Muzyka zostanie automatycznie pobrana przez useEffect po aktualizacji w Firestore
        setIsUploading(false);
      } catch (err) {
        console.error("Błąd podczas uploadu muzyki:", err);
        setError(err instanceof Error ? err.message : "Nieznany błąd podczas uploadu");
        setIsUploading(false);
      }
    },
    [tournamentId, categoryId, presentationId]
  );

  // Funkcja usuwania muzyki
  const deleteMusic = useCallback(
    async () => {
      if (!tournamentId || !categoryId || !presentationId || !music) {
        setError("Brak wymaganych danych do usunięcia");
        return;
      }

      setIsDeleting(true);
      setError(null);

      try {
        const result = await deleteMusicFile(tournamentId, categoryId, presentationId, music.id, app);

        if (!result.success) {
          setError(result.error || "Nie udało się usunąć pliku");
        }
        // Muzyka zostanie automatycznie usunięta z state przez useEffect po usunięciu w Firestore
      } catch (err) {
        console.error("Błąd podczas usuwania muzyki:", err);
        setError(err instanceof Error ? err.message : "Nieznany błąd podczas usuwania");
      } finally {
        setIsDeleting(false);
      }
    },
    [tournamentId, categoryId, presentationId, music]
  );

  return {
    music,
    isUploading,
    isDeleting,
    isLoading,
    uploadMusic,
    deleteMusic,
    error,
  };
}

